import React, { useState } from 'react';
import Loader from '../../generic-components/loader/Loader';
import { useForm } from 'react-hook-form';
import ClayLayout from '@clayui/layout';
import {
    pitchFormBasicDetailsFields,
    pitchFormPOCDetailsFields
} from '../../utils/PitchFormConfig';
import FormComponent from '../../generic-components/formComponent/FormComponent';
import Button from '@clayui/button';
import { primaryButtonStyle } from '../../utils/commonUtil';
import IndustriesSelect from '../IndustriesSelect/IndustriesSelect';
import { defaultValueForSelect, scopeKeyPitchForm } from '../../utils/Config';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { updatePitchFormValues } from '../../slices/pitchFormSlice';
import { updateCommonValues } from '../../slices/commonSlice';
import { useEffect } from 'react';
import { otpService } from '../../services/otpService';
import { toast } from 'react-toastify';
import { useFetchListTypeEntries } from '../../services/ListTypeEntryService';

interface IPitchFormCompanyDetail {
    setStep: React.Dispatch<React.SetStateAction<number>>;
}
const PitchFormCompanyDetail: React.FC<IPitchFormCompanyDetail> = ({
    setStep
}) => {
    const [loading, setLoading] = useState(false);
    const [industryOptions, setIndustryOptions] = useState<any[]>([]);
    const [subIndustryOptions, setSubIndustryOptions] = useState<any[]>([]);
    const pitchFormData = useSelector((state: RootState) => state.pitchForm);
    const {
        control,
        register,
        formState: { errors: formErrors },
        watch,
        handleSubmit,
        setError,
        setValue,
        getValues
    } = useForm<any>({
        mode: 'all',
        defaultValues: { ...pitchFormData }
    });
    const dispatch = useDispatch();
    const annualRevenue = useFetchListTypeEntries('ANNUAL_REVENUE');
    const [uploadCompanyProfile, setUploadCompanyProfile] = useState<File[]>(
        pitchFormData?.companyProfile || []
    );
    const fileHandling = {
        companyProfile: {
            selectedFiles: uploadCompanyProfile,
            setSelectedFiles: setUploadCompanyProfile
        }
    };
    const dropdownOptions = {
        annualTurnover: annualRevenue
    };
    const industryValue = watch('industry');
    const managedFields = React.useMemo(
        () => [
            ...pitchFormBasicDetailsFields.map((f) => f.name),
            ...pitchFormPOCDetailsFields.map((f) => f.name),
            'industrySubcategory'
        ],
        []
    );

    const getManagedValues = (currentValues: any) => {
        const payload: any = {};
        managedFields.forEach((name) => {
            if (currentValues[name] !== undefined) {
                payload[name] = currentValues[name];
            }
        });
        payload.companyProfile = uploadCompanyProfile;
        // Overwrite file fields with local state if managed manually
        // Note: form inputs for files might be empty/string, but we want the File[] state
        return payload;
    };
    const onSubmit = async (data: any) => {
        setLoading(true);
        const values = getManagedValues(getValues());
        dispatch(
            updatePitchFormValues({
                ...values,
                industryOptions,
                subIndustryOptions
            })
        );
        dispatch(
            updateCommonValues({
                businessEmail: data.contactEmail,
                industry: data.industry,
                industrySubcategory: data.industrySubcategory
            })
        );
        const response = await otpService.gerateOTP(
            data?.contactEmail,
            scopeKeyPitchForm
        );
        if (response.statusCode === 200) {
            toast.success(response.statusMessage, {
                theme: 'light',
                autoClose: 2000
            });
            setStep(1);
        }
        setLoading(false);
    };

    const newPitchFormBasicDetailsFields = pitchFormBasicDetailsFields?.filter(
        (field) => {
            if (field?.name == 'otherBusinessSector') {
                const otherId = industryOptions?.find(
                    (item: any) => item?.name?.trim() === 'Other'
                )?.key;
                if (industryValue?.includes(otherId)) {
                    return true;
                }
                return false;
            }
            return true;
        }
    );
    return (
        <>
            {loading && <Loader />}
            <form onSubmit={handleSubmit(onSubmit)}>
                <ClayLayout.Row justify="start" className="basic_detail_form">
                    {newPitchFormBasicDetailsFields.map((field) => {
                        const colSize = ['industry']?.includes(field?.name)
                            ? 12
                            : 6;
                        return field?.name == 'industry' ? (
                            <IndustriesSelect
                                key={field.name}
                                control={control}
                                watch={watch}
                                errors={formErrors}
                                setValue={setValue}
                                register={register}
                                onIndustryOptionsChange={setIndustryOptions}
                                onSubIndustryOptionsChange={
                                    setSubIndustryOptions
                                }
                            />
                        ) : (
                            <ClayLayout.Col
                                key={field.name}
                                xl={colSize}
                                lg={colSize}
                                md={colSize}
                                sm={colSize}
                                xs={12}
                            >
                                <FormComponent
                                    {...field}
                                    register={register}
                                    control={control}
                                    errors={formErrors}
                                    watch={watch}
                                    setError={setError}
                                    errorTitle={field.errorMessage}
                                    options={
                                        (dropdownOptions as any)[field.name] ||
                                        []
                                    }
                                    selectedFiles={
                                        fileHandling[
                                            field.name as keyof typeof fileHandling
                                        ]?.selectedFiles
                                    }
                                    setSelectedFiles={
                                        fileHandling[
                                            field.name as keyof typeof fileHandling
                                        ]?.setSelectedFiles
                                    }
                                />
                            </ClayLayout.Col>
                        );
                    })}
                </ClayLayout.Row>
                <>
                    <h2 className="section-title">Point of Contact Details:</h2>
                    <ClayLayout.Row
                        justify="start"
                        className="basic_detail_form"
                    >
                        {pitchFormPOCDetailsFields.map((field) => (
                            <ClayLayout.Col
                                key={field.name}
                                xl={6}
                                lg={6}
                                md={6}
                                sm={6}
                                xs={12}
                            >
                                <FormComponent
                                    {...field}
                                    register={register}
                                    control={control}
                                    errors={formErrors}
                                    watch={watch}
                                    setError={setError}
                                    errorTitle={field.errorMessage}
                                    options={
                                        (dropdownOptions as any)[field.name] ||
                                        []
                                    }
                                />
                            </ClayLayout.Col>
                        ))}
                    </ClayLayout.Row>
                </>
                <div className="basic-details-button-section">
                    <Button
                        type="submit"
                        className="bottom-cut"
                        style={primaryButtonStyle}
                    >
                        <span className="bottom-cut-span"></span>
                        Continue
                    </Button>
                </div>
            </form>
        </>
    );
};

export default PitchFormCompanyDetail;
